import React from 'react';
import {Box, Button, Card, CardContent, Chip, Typography} from '@mui/material';
import {
    Photo as PhotoIcon,
    TextFields as TextIcon,
    TrendingUp as ResearchIcon,
    VideoLibrary as VideoIcon,
    AutoAwesome as FunnelIcon
} from '@mui/icons-material';
import {useNavigate, useParams} from 'react-router-dom';

import ResearchTestPage from './ResearchTestPage';
import ImageTestPage from './ImageTestPage';
import DescriptionTestPage from './DescriptionTestPage';
import VideoGenerationTestPage from './VideoGenerationTestPage';
import FunnelTestPage from './FunnelTestPage';
import { ResponsiveCardGrid } from '../components/common/ResponsiveLayout';

interface TestModule {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    status: 'ready' | 'in_progress' | 'planned';
    component?: React.ComponentType;
}

const testModules: TestModule[] = [
    {
        id: 'research',
        title: 'Product Research',
        description: 'Test the research system with dynamic prompts. Select a product, marketplace, and AI service.',
        icon: <ResearchIcon fontSize="large" color="primary"/>,
        status: 'ready',
        component: ResearchTestPage
    },
/*    {
        id: 'description',
        title: 'Description Generation',
        description: 'Test the module for creating product descriptions based on research results.',
        icon: <TextIcon fontSize="large" color="primary"/>,
        status: 'ready',
        component: DescriptionTestPage,
    },*/
    {
        id: 'image_generation',
        title: 'Image Generation',
        description: 'Test the system for creating product images using AI (e.g., Flux, Imagen).',
        icon: <PhotoIcon fontSize="large" color="primary"/>,
        status: 'ready',
        component: ImageTestPage
    },
    {
        id: 'funnel_generation',
        title: 'Funnel Generation',
        description: 'Test AI-powered photo funnels creation for marketplaces like Uzum.',
        icon: <FunnelIcon fontSize="large" color="primary" />,
        status: 'ready',
        component: FunnelTestPage,
    },
    {
        id: 'video_generation',
        title: 'Video Generation',
        description: 'Test the creation of video content for products and social media.',
        icon: <VideoIcon fontSize="large" color="primary"/>,
        status: 'ready',
        component: VideoGenerationTestPage,
    }
];

const TestsPage: React.FC = () => {
    const navigate = useNavigate();
    const { activeTestId } = useParams<{ activeTestId?: string }>();

    const getStatusColor = (status: TestModule['status']): 'success' | 'warning' | 'default' => {
        switch (status) {
            case 'ready': return 'success';
            case 'in_progress': return 'warning';
            case 'planned': default: return 'default';
        }
    };

    const getStatusText = (status: TestModule['status']) => {
        switch (status) {
            case 'ready': return 'Ready';
            case 'in_progress': return 'In Progress';
            case 'planned': default: return 'Planned';
        }
    };

    if (activeTestId) {
        const activeTestModule = testModules.find(t => t.id === activeTestId);
        if (activeTestModule?.component) {
            const TestComponent = activeTestModule.component;
            return (
                <Box>
                    <Box sx={{ mb: 3 }}>
                        <Button onClick={() => navigate('/dashboard/tests')} variant="outlined" color="primary">
                            ← Back to tests list
                        </Button>
                    </Box>
                    <TestComponent/>
                </Box>
            );
        }
    }

    return (
        <Box>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1">Module Testing</Typography>
                <Typography color="text.secondary">
                    Testing various components and modules of the EcomAI system.
                </Typography>
            </Box>

            <ResponsiveCardGrid>
                {testModules.map((testModule) => (
                    <Card
                        key={testModule.id}
                        sx={{ display: 'flex', flexDirection: 'column', cursor: testModule.status === 'ready' ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (testModule.component && testModule.status === 'ready') {
                                navigate(`/dashboard/tests/${testModule.id}`);
                            }
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{mb: 2, display: 'flex', alignItems: 'center', gap: 2}}>
                                {testModule.icon}
                                <Typography variant="h5" component="h2">{testModule.title}</Typography>
                            </Box>
                            <Chip
                                label={getStatusText(testModule.status)}
                                color={getStatusColor(testModule.status)}
                                size="small"
                                sx={{mb: 2}}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {testModule.description}
                            </Typography>
                        </CardContent>
                    </Card>
                ))}
            </ResponsiveCardGrid>
        </Box>
    );
};

export default TestsPage;
